import React, { useState } from "react";
import { Box, Flex } from "theme-ui";

import { themevals } from "theme/themevals";
import BasicButton from "components/forms/BasicButton";
import BasicModal from "components/ui/BasicModal";
import { useToast } from "components/toast";
import { bulkLeads, toDueDate, LEAD_STATUS_LABELS, type BulkLeadAction, type LeadRow, type LeadStatus } from "api/Leads";

/**
 * The bar that appears over the leads list when rows are ticked. One
 * action at a time, applied to every selected lead through the same rules
 * as the record page (Won is set by Convert only, lost clears the next
 * action, a lead with a project or invoice cannot be deleted, a hand-off
 * emails the new owner). Failures are listed by name, not swallowed.
 * Export needs no server call - it writes the selected rows to a CSV.
 */

const input: React.CSSProperties = {
  padding: "7px 9px",
  border: `1px solid ${themevals.colors.inputOutline}`,
  borderRadius: 6,
  fontSize: 13,
  fontFamily: "Poppins",
  background: "#fff",
};

type Mode = null | "assign" | "status" | "next_action" | "lost" | "delete";

export default function LeadsBulkBar({
  selected,
  owners,
  onClear,
  onDone,
}: {
  selected: LeadRow[];
  owners: Array<{ id: string; name: string }>;
  onClear: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const [mode, setMode] = useState<Mode>(null);
  const [busy, setBusy] = useState(false);
  const [owner, setOwner] = useState("");
  const [status, setStatus] = useState<LeadStatus>("contacted");
  const [at, setAt] = useState("");
  const [note, setNote] = useState("");
  const [reason, setReason] = useState("");
  const [failures, setFailures] = useState<Array<{ name?: string; reason: string }>>([]);

  const n = selected.length;
  if (!n) return null;

  const run = async (action: BulkLeadAction, value?: any) => {
    setBusy(true);
    setFailures([]);
    const res: any = await bulkLeads(selected.map((r) => r.id), action, value);
    setBusy(false);
    if (res?.error) return toast.add(res.error, "var(--theme-ui-colors-danger)");
    const verb = { assign: "reassigned", status: "updated", next_action: "given a next action", lost: "marked lost", delete: "deleted" }[action];
    toast.add(`${res.done} lead${res.done === 1 ? "" : "s"} ${verb}${res.failed.length ? `, ${res.failed.length} skipped` : ""}.`, res.failed.length ? "var(--theme-ui-colors-warning)" : "var(--theme-ui-colors-success)");
    setFailures(res.failed);
    setMode(null);
    onDone();
  };

  const exportCsv = () => {
    const cols: Array<[string, (r: LeadRow) => any]> = [
      ["Organization", (r) => r.name],
      ["Contact", (r) => r.contactName],
      ["Email", (r) => r.contactEmail],
      ["Phone", (r) => r.phone],
      ["Status", (r) => LEAD_STATUS_LABELS[r.leadStatus] ?? r.leadStatus],
      ["Owner", (r) => r.ownerName],
      ["Source", (r) => r.leadSource],
      ["Next action", (r) => r.nextActionNote],
      ["Next action due", (r) => (r.nextActionAt ? new Date(r.nextActionAt).toLocaleDateString("en-US") : "")],
      ["Sites", (r) => r.siteCount],
      ["RFQs", (r) => r.rfqCount],
      ["Quotes", (r) => r.quoteCount],
      ["Proposals", (r) => r.proposalCount],
      ["Last activity", (r) => r.lastActivitySummary],
    ];
    const esc = (v: any) => {
      const s = v == null ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [cols.map(([h]) => h).join(","), ...selected.map((r) => cols.map(([, f]) => esc(f(r))).join(","))].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const Action = ({ label, m, danger }: { label: string; m: Mode; danger?: boolean }) => (
    <button
      type="button"
      onClick={() => setMode(mode === m ? null : m)}
      style={{
        background: mode === m ? themevals.colors.black : "#fff",
        color: mode === m ? "#fff" : danger ? "#A60000" : themevals.colors.black,
        border: `1px solid ${mode === m ? themevals.colors.black : themevals.colors.inputOutline}`,
        borderRadius: 14,
        padding: "5px 12px",
        fontFamily: "Poppins",
        fontSize: 12,
        cursor: "pointer",
      }}
    >
      {label}
    </button>
  );

  return (
    <Box
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 5,
        marginBottom: "12px",
        padding: "12px 16px",
        background: "#FAFAF8",
        border: "1px solid",
        borderColor: "inputOutline",
        borderRadius: "8px",
        fontFamily: "Poppins",
      }}
    >
      <Flex sx={{ alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
        <Box sx={{ fontSize: "13px", fontWeight: 600, color: themevals.colors.black, marginRight: "6px" }}>
          {n} selected
        </Box>
        <Action label="Assign owner" m="assign" />
        <Action label="Change status" m="status" />
        <Action label="Set next action" m="next_action" />
        <Action label="Mark lost" m="lost" />
        <button type="button" onClick={exportCsv} style={{ background: "#fff", color: themevals.colors.black, border: `1px solid ${themevals.colors.inputOutline}`, borderRadius: 14, padding: "5px 12px", fontFamily: "Poppins", fontSize: 12, cursor: "pointer" }}>
          Export CSV
        </button>
        <Action label="Delete" m="delete" danger />
        <Box sx={{ flex: 1 }} />
        <button type="button" onClick={() => { setMode(null); setFailures([]); onClear(); }} style={{ background: "none", border: "none", color: "#185FA5", fontFamily: "Poppins", fontSize: 12, fontWeight: 500, cursor: "pointer" }}>
          Clear selection
        </button>
      </Flex>

      {mode === "assign" && (
        <Flex sx={{ gap: "8px", alignItems: "center", marginTop: "10px", flexWrap: "wrap" }}>
          <select style={input} value={owner} onChange={(e) => setOwner(e.target.value)}>
            <option value="">Nobody</option>
            {owners.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
          <BasicButton type="button" $submitting={busy} disabled={busy} onClick={() => run("assign", owner || null)}>Assign {n}</BasicButton>
        </Flex>
      )}
      {mode === "status" && (
        <Flex sx={{ gap: "8px", alignItems: "center", marginTop: "10px", flexWrap: "wrap" }}>
          <select style={input} value={status} onChange={(e) => setStatus(e.target.value as LeadStatus)}>
            {(["new", "contacted", "qualified", "proposal_sent", "negotiating", "dormant"] as LeadStatus[]).map((s) => (
              <option key={s} value={s}>{LEAD_STATUS_LABELS[s]}</option>
            ))}
          </select>
          <BasicButton type="button" $submitting={busy} disabled={busy} onClick={() => run("status", status)}>Update {n}</BasicButton>
          <Box sx={{ fontSize: "11px", color: themevals.colors.dkgrey }}>Won comes from Convert; Lost has its own action.</Box>
        </Flex>
      )}
      {mode === "next_action" && (
        <Flex sx={{ gap: "8px", alignItems: "center", marginTop: "10px", flexWrap: "wrap" }}>
          <input style={{ ...input, minWidth: 260 }} placeholder="What to do next" value={note} onChange={(e) => setNote(e.target.value)} />
          <input style={input} type="date" value={at} onChange={(e) => setAt(e.target.value)} />
          <BasicButton type="button" $submitting={busy} disabled={busy || (!note.trim() && !at)} onClick={() => run("next_action", { at: toDueDate(at), note: note.trim() || null })}>Set on {n}</BasicButton>
        </Flex>
      )}
      {mode === "lost" && (
        <Flex sx={{ gap: "8px", alignItems: "center", marginTop: "10px", flexWrap: "wrap" }}>
          <input style={{ ...input, minWidth: 300 }} placeholder="Why (optional) - price, timing, went elsewhere" value={reason} onChange={(e) => setReason(e.target.value)} />
          <BasicButton type="button" $submitting={busy} disabled={busy} onClick={() => run("lost", reason.trim() || null)}>Mark {n} lost</BasicButton>
        </Flex>
      )}

      {mode === "delete" && (
        <BasicModal
          title={`Delete ${n} lead${n === 1 ? "" : "s"}?`}
          cancel_click={() => setMode(null)}
          cancel_text="Cancel"
          confirm_text={busy ? "Deleting\u2026" : `Delete ${n}`}
          submitting={busy}
          confirm_click={() => run("delete")}
        >
          <Box sx={{ fontFamily: "Poppins", fontSize: "13px", textAlign: "left", color: themevals.colors.black }}>
            They leave the list and search and their public quote links stop working. Any lead with a project or an invoice is skipped and listed. For prospects that went elsewhere, Mark lost keeps them for reporting.
          </Box>
        </BasicModal>
      )}

      {failures.length > 0 && (
        <Box sx={{ marginTop: "10px", fontSize: "12px", color: "#A60000" }}>
          {failures.map((f, i) => (
            <div key={i}>{f.name ?? "A lead"}: {f.reason}</div>
          ))}
        </Box>
      )}
    </Box>
  );
}
